/** @file mesh.smoothWeights: Laplacian smoothing of skinned weights over triangle edges (document-only). */
import { z } from 'zod';
import type { Id } from '@pose2d/format/types';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import { editWeights } from './editWeights';
import { meshNeighbors } from './meshNeighbors';
import { normalizeVertexWeights } from './normalizeVertexWeights';

type Influences = [Id, number][];

/** One Jacobi pass: each target vertex keeps (1 − strength) of its weights and takes strength × its neighbours' mean. */
function smoothPass(weights: readonly Influences[], neighbors: readonly number[][], targets: readonly number[], strength: number): Influences[] {
  const next = weights.map((v) => v.map(([b, w]): [Id, number] => [b, w]));
  for (const v of targets) {
    const around = neighbors[v] ?? [];
    if (around.length === 0) continue;
    const mixed: Influences = (weights[v] ?? []).map(([b, w]): [Id, number] => [b, w * (1 - strength)]);
    for (const u of around) for (const [b, w] of weights[u] ?? []) mixed.push([b, (w * strength) / around.length]);
    next[v] = normalizeVertexWeights(mixed) ?? next[v] ?? [];
  }
  return next;
}

/** mesh.smoothWeights: `iterations` Jacobi passes over the listed vertices (all by default), then cap and normalize. */
export const command = defineCommand({
  name: 'mesh.smoothWeights',
  description: "Smooth a skinned mesh's weights over its triangle edges: each pass moves every listed vertex (all by default) `strength` (default 0.5) of the way to its neighbours' mean, for `iterations` passes (default 1, max 20), then caps at 4 influences and normalizes.",
  input: z
    .object({
      target: z.string().min(1),
      iterations: z.number().int().min(1).max(20).exactOptional(),
      strength: prim.unit.exactOptional(),
      vertices: z.array(z.number().int().nonnegative()).exactOptional(),
    })
    .strict(),
  apply(draft, input, ctx) {
    editWeights(draft, ctx, input.target, (g, mesh) => {
      const count = mesh.uvs.length / 2;
      const targets = input.vertices ?? Array.from({ length: count }, (_, i) => i);
      const bad = targets.findIndex((v) => v >= count);
      if (bad >= 0) throwError('VALIDATION', undefined, { path: `vertices.${bad}`, hint: `vertex indices must be < ${count}` });
      const neighbors = meshNeighbors(count, mesh.triangles);
      let weights: Influences[] = g.weights.map((v) => v.map(([b, w]): [Id, number] => [b, w]));
      for (let i = 0; i < (input.iterations ?? 1); i++) weights = smoothPass(weights, neighbors, targets, input.strength ?? 0.5);
      return weights;
    });
  },
});
